import { apiFetch } from '@/lib/api';

export interface NormalizedAttendee {
  // Required fields
  first_name: string;
  last_name: string;
  full_name: string;
  
  // Optional contact fields  
  email?: string;
  phone?: string;
  
  // Location fields (for geocoding)
  city?: string;
  region?: string;
  country_iso2?: string;
  postal_code?: string;
  address_line?: string;
  
  // Metadata
  external_ref?: string;
  raw_data: { [key: string]: string | undefined };
}

export interface ImportError {
  row_index: number;
  field?: string;
  message: string;
  raw_data?: { [key: string]: string | undefined };
}

export interface ImportDraftSummary {
  id: string;
  filename: string;
  total_rows: number;
  normalized_count: number;
  error_count: number;
  status: 'pending' | 'ready' | 'committed' | 'cancelled';
  created_at: Date;
  expires_at?: Date;
}

export interface ImportPreview {
  draft: ImportDraftSummary;
  normalized_attendees: NormalizedAttendee[];
  errors: ImportError[];
}

export interface ImportCommitResult {
  success: boolean;
  inserted: number;
  updated: number;
  skipped: number;
  errors: Array<{
    attendee_index: number;
    message: string;
  }>;
}

export interface ImportStartResponse {
  draft_id: string;
}

export class AttendeeImportService {
  /**
   * Start CSV import process
   */
  async startImport(eventId: string, file: File): Promise<ImportStartResponse> {
    const formData = new FormData();
    formData.append('csv', file);
    
    const response = await apiFetch(`/api/events/${eventId}/attendees/import`, {
      method: 'POST',
      body: formData,
      // Don't set Content-Type header - let browser set it for FormData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: { message: 'Import failed to start' } }));
      throw new Error(errorData.error?.message || 'Import failed to start');
    }

    const data = await response.json();
    return data.data as ImportStartResponse;
  }

  /**
   * Get import preview with normalized data
   */
  async getImportPreview(eventId: string, draftId: string): Promise<ImportPreview> {
    console.log(`📡 Fetching preview for draft ${draftId}`);
    
    try {
      const response = await apiFetch(`/api/events/${eventId}/attendees/import/${draftId}`);
      console.log(`📡 Preview response:`, { 
        ok: response.ok, 
        status: response.status
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: { message: 'Failed to get import preview' } }));
        throw new Error(errorData.error?.message || 'Failed to get import preview');
      }

      const data = await response.json();
      return data.data as ImportPreview;
    } catch (error) {
      console.error(`❌ getImportPreview failed:`, error);
      throw error;
    }
  }

  /**
   * Commit import to database
   */
  async commitImport(eventId: string, draftId: string): Promise<ImportCommitResult> {
    const response = await apiFetch(`/api/events/${eventId}/attendees/import/${draftId}/commit`, {
      method: 'POST',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: { message: 'Import commit failed' } }));
      throw new Error(errorData.error?.message || 'Import commit failed');
    }

    const data = await response.json();
    return data.data as ImportCommitResult;
  }

  /**
   * Cancel/delete import draft
   */
  async cancelImport(eventId: string, draftId: string): Promise<void> {
    const response = await apiFetch(`/api/events/${eventId}/attendees/import/${draftId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: { message: 'Failed to cancel import' } }));
      throw new Error(errorData.error?.message || 'Failed to cancel import');
    }
  }

  /**
   * Poll import status until ready or failed
   */
  async pollImportStatus(
    eventId: string, 
    draftId: string, 
    options: {
      maxAttempts?: number;
      intervalMs?: number;
      onStatusUpdate?: (status: string) => void;
    } = {}
  ): Promise<ImportPreview> {
    const maxAttempts = options.maxAttempts || 60; // 2 minutes with 2s intervals
    const intervalMs = options.intervalMs || 2000;
    
    console.log(`🔄 Starting polling for draft ${draftId}, max attempts: ${maxAttempts}`);
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        console.log(`📊 Poll attempt ${attempt + 1}/${maxAttempts} for draft ${draftId}`);
        const preview = await this.getImportPreview(eventId, draftId);
        console.log(`📋 Poll result:`, { 
          status: preview.draft.status, 
          normalizedCount: preview.normalized_attendees.length,
          errorCount: preview.errors.length 
        });
        
        if (options.onStatusUpdate) {
          options.onStatusUpdate(preview.draft.status);
        }

        if (preview.draft.status === 'ready' || preview.draft.status === 'cancelled') {
          console.log(`✅ Polling completed with status: ${preview.draft.status}`);
          return preview;
        }

        // Wait before next poll
        console.log(`⏳ Waiting ${intervalMs}ms before next poll...`);
        await this.sleep(intervalMs);
      } catch (error) {
        console.warn(`⚠️ Import status poll attempt ${attempt + 1} failed:`, error);
        // Continue polling unless it's the last attempt
        if (attempt === maxAttempts - 1) {
          console.error(`❌ Polling failed after ${maxAttempts} attempts:`, error);
          throw error;
        }
        await this.sleep(intervalMs);
      }
    }

    console.error(`⏰ Import processing timed out after ${maxAttempts} attempts`);
    throw new Error('Import processing timed out');
  }

  /**
   * Utility sleep function
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Validate CSV file before upload
   */
  validateFile(file: File): { valid: boolean; error?: string } {
    // Check file type
    if (!file.name.toLowerCase().endsWith('.csv') && file.type !== 'text/csv') {
      return { valid: false, error: 'File must be a CSV file' };
    }

    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      return { valid: false, error: 'File size must be less than 10MB' };
    }

    // Check for empty file
    if (file.size === 0) {
      return { valid: false, error: 'File cannot be empty' };
    }

    return { valid: true };
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Generate CSV template for download
   */
  generateCSVTemplate(): string {
    const headers = [
      'first_name',
      'last_name', 
      'email',
      'phone',
      'city',
      'state_province',
      'country',
      'postal_code',
      'address',
      'employee_id'
    ];

    const sampleRows = [
      [
        'John',
        'Smith',
        'john.smith@company.com',
        '+1 (555) 123-4567',
        'Los Angeles',
        'California',
        'US',
        '90210',
        '123 Main St, Los Angeles, CA 90210',
        'EMP001'
      ],
      [
        'Jane',
        'Doe',
        'jane.doe@company.com',
        '+1 (555) 987-6543',
        'New York',
        'New York',
        'US',
        '10001',
        '456 Broadway, New York, NY 10001',
        'EMP002'
      ]
    ];

    const csvContent = [
      headers.join(','),
      ...sampleRows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    return csvContent;
  }

  /**
   * Download CSV template
   */
  downloadCSVTemplate(): void {
    const csvContent = this.generateCSVTemplate();
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'attendee-import-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }
}